import { useCallback, useEffect, useState } from "react";
import {
  friendsApi,
  friendlyError,
  type Friend,
  type KycFileKind,
  type KycLabel,
  type ProfileIn,
} from "@/lib/api";
import { Badge, Button, Card, ErrorText, Field, PageLoader, Row } from "@/components/ui";

const KYC: Record<KycLabel, { label: string; tone: "green" | "amber" | "red" | "muted" }> = {
  none: { label: "Нет данных", tone: "muted" },
  draft: { label: "Черновик", tone: "muted" },
  pending: { label: "На проверке", tone: "amber" },
  verified: { label: "Проверен", tone: "green" },
  rejected: { label: "Отказ", tone: "red" },
};

export function Friends() {
  const [list, setList] = useState<Friend[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    friendsApi.list().then((r) => setList(r.friends)).catch((e) => setErr(friendlyError(e)));
  }, []);
  useEffect(load, [load]);

  if (selected) return <FriendDetail id={selected} onBack={() => { setSelected(null); load(); }} />;
  if (err && list === null) return <Card className="mt-4"><ErrorText>{err}</ErrorText></Card>;
  if (list === null) return <PageLoader />;

  return (
    <div className="space-y-3 pt-2">
      <div>
        <div className="text-xl font-bold">Друзья</div>
        <p className="mt-1 text-sm text-white/45">Управляемые аккаунты — вы ведёте их сами.</p>
      </div>
      <AddFriend onAdded={load} />
      {list.length === 0 && <Card><p className="text-sm text-white/50">Пока нет друзей. Добавьте первого.</p></Card>}
      {list.map((f) => (
        <button key={f.id} type="button" className="block w-full text-left" onClick={() => setSelected(f.id)}>
          <Card>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium">
                  {[f.first_name, f.last_name].filter(Boolean).join(" ") || f.phone || "Друг"}
                </div>
                <div className="text-xs text-white/40">{f.phone}</div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Badge tone={KYC[f.kyc].tone}>{KYC[f.kyc].label}</Badge>
                {f.broker?.client_id && (
                  <span className="text-[11px] text-white/40">
                    cid {f.broker.client_id}{f.broker.signed ? " · ДБО ✓" : ""}
                  </span>
                )}
              </div>
            </div>
          </Card>
        </button>
      ))}
    </div>
  );
}

function AddFriend({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ phone: "", email: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!f.phone.trim() || !f.email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await friendsApi.create({
        phone: f.phone.trim(),
        email: f.email.trim(),
        first_name: f.first_name.trim() || undefined,
        last_name: f.last_name.trim() || undefined,
      });
      setF({ phone: "", email: "", first_name: "", last_name: "" });
      setOpen(false);
      onAdded();
    } catch (e) {
      setError(friendlyError(e, "Не удалось добавить друга."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return <Button className="w-full" onClick={() => setOpen(true)}>+ Добавить друга</Button>;
  return (
    <Card className="space-y-3">
      <div className="text-sm font-medium text-white/70">Новый друг</div>
      <Field label="Телефон" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+7…" />
      <Field label="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="mail@example.com" />
      <Field label="Имя" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
      <Field label="Фамилия" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
      <ErrorText>{error}</ErrorText>
      <div className="flex gap-2">
        <Button className="flex-1" loading={busy} disabled={!f.phone.trim() || !f.email.trim()} onClick={submit}>Добавить</Button>
        <Button variant="ghost" onClick={() => { setOpen(false); setError(""); }}>Отмена</Button>
      </div>
    </Card>
  );
}

function FriendDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [friend, setFriend] = useState<Friend | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    friendsApi.get(id).then(setFriend).catch((e) => setErr(friendlyError(e)));
  }, [id]);
  useEffect(load, [load]);

  if (err) return <Card className="mt-4"><ErrorText>{err}</ErrorText></Card>;
  if (!friend) return <PageLoader />;

  return (
    <div className="space-y-3 pt-2">
      <button type="button" onClick={onBack} className="text-sm text-brand">← Все друзья</button>
      <Card>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">
            {[friend.first_name, friend.last_name].filter(Boolean).join(" ") || friend.phone}
          </div>
          <Badge tone={KYC[friend.kyc].tone}>{KYC[friend.kyc].label}</Badge>
        </div>
        <div className="mt-2">
          <Row left={<span className="text-white/50">Телефон</span>} right={friend.phone} />
          <Row left={<span className="text-white/50">Email</span>} right={friend.email} />
          {friend.broker?.client_id && (
            <Row left={<span className="text-white/50">Брокер</span>} right={`cid ${friend.broker.client_id} · ${friend.broker.agreement_status || "—"}`} />
          )}
        </div>
      </Card>

      {!friend.broker?.client_id ? (
        <KycForm friend={friend} onChanged={load} />
      ) : !friend.broker.signed ? (
        <Agreement friend={friend} onChanged={load} />
      ) : (
        <Operations friend={friend} onChanged={load} />
      )}
    </div>
  );
}

function Agreement({ friend, onChanged }: { friend: Friend; onChanged: () => void }) {
  const [stage, setStage] = useState<"idle" | "otp">("idle");
  const [ctx, setCtx] = useState<import("@/lib/api").SignContext | null>(null);
  const [otpId, setOtpId] = useState("");
  const [otp, setOtp] = useState("");
  const [relayedCode, setRelayedCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const start = async () => {
    setBusy(true); setError("");
    try {
      const r = await friendsApi.agreementSendOtp(friend.id);
      if (r.already_signed) { onChanged(); return; }
      setCtx(r.sign_context); setOtpId(r.otp_id); setRelayedCode(null); setStage("otp");
    } catch (e) { setError(friendlyError(e, "Не удалось запросить код.")); } finally { setBusy(false); }
  };

  // Код приходит на нашу прокси-почту → показываем его хосту здесь (и дублируем в Telegram хоста).
  useEffect(() => {
    if (stage !== "otp") return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await friendsApi.agreementOtp(friend.id);
        if (alive && r.code) setRelayedCode(r.code);
      } catch { /* ignore */ }
    };
    void tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [stage, friend.id]);
  const confirm = async () => {
    if (!ctx || otp.trim().length < 4) return;
    setBusy(true); setError("");
    try {
      await friendsApi.agreementConfirm(friend.id, ctx, otpId, otp.trim());
      onChanged();
    } catch (e) { setError(friendlyError(e, "Неверный код. Попробуйте ещё раз.")); } finally { setBusy(false); }
  };

  return (
    <Card className="space-y-3">
      <div className="text-sm font-medium text-white/70">Договор с брокером (ДБО)</div>
      <p className="text-sm text-white/50">
        Подпишите ДБО за друга. Код появится здесь и продублируется вам в Telegram.
      </p>
      {stage === "otp" && (
        <>
          {relayedCode && (
            <button
              type="button"
              onClick={() => setOtp(relayedCode)}
              className="w-full rounded-xl border border-brand/40 bg-brand/10 px-4 py-3 text-center active:opacity-80"
            >
              <div className="text-xs text-white/60">Код подтверждения</div>
              <div className="mt-0.5 text-2xl font-semibold tracking-[0.3em] text-brand">{relayedCode}</div>
              <div className="mt-0.5 text-xs text-white/45">Нажмите, чтобы подставить</div>
            </button>
          )}
          <Field label="Код" value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" placeholder="000000" />
        </>
      )}
      <ErrorText>{error}</ErrorText>
      {stage === "idle" ? (
        <Button className="w-full" loading={busy} onClick={start}>Запросить код</Button>
      ) : (
        <div className="flex gap-2">
          <Button className="flex-1" loading={busy} disabled={otp.trim().length < 4} onClick={confirm}>Подписать</Button>
          <Button variant="ghost" loading={busy} onClick={start}>Новый код</Button>
        </div>
      )}
    </Card>
  );
}

function Operations({ friend, onChanged }: { friend: Friend; onChanged: () => void }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [order, setOrder] = useState<import("@/lib/api").OrderView | null>(null);

  const rub = (n: number) => `${(n ?? 0).toLocaleString("ru-RU")} ₽`;

  const makeOrder = async () => {
    const v = Number(amount.replace(",", "."));
    if (!Number.isFinite(v) || v <= 0 || busy) return;
    setBusy(true); setError(""); setMsg(""); setOrder(null);
    try {
      const o = await friendsApi.createOrder(friend.id, v);
      setOrder(o);
      setMsg("Заявка создана — оплатите по реквизитам, зачисление автоматическое.");
      onChanged();
    } catch (e) { setError(friendlyError(e, "Не удалось создать заявку.")); } finally { setBusy(false); }
  };
  const doWithdraw = async () => {
    if (busy) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const w = await friendsApi.withdraw(friend.id, friend.account_rub);
      setMsg(`Вывод ${w.reference} запущен: ≈${w.net_usdt} USDT на ваш кошелёк.`);
      onChanged();
    } catch (e) { setError(friendlyError(e, "Не удалось создать вывод.")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <Card>
        <div className="text-xs text-white/45">Баланс друга</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{rub(friend.account_rub)}</div>
      </Card>

      <Card className="space-y-3">
        <div className="text-sm font-medium text-white/70">Заявка на оплату</div>
        <Field label="Сумма, ₽" type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Button className="w-full" loading={busy} onClick={makeOrder}>Создать заявку</Button>
        {order?.requisites && (
          <div className="rounded-xl bg-surface-raised p-3 text-xs text-white/70">
            <div className="mb-1 font-medium text-white/80">Реквизиты для оплаты</div>
            {Object.entries(order.requisites).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 py-0.5">
                <span className="text-white/45">{k}</span>
                <span className="text-right">{String(v)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="space-y-3">
        <div className="text-sm font-medium text-white/70">Вывод крипты на ваш кошелёк</div>
        <p className="text-xs text-white/45">
          Выведет весь баланс ({rub(friend.account_rub)}) на ваш адрес, удержание 3 USDT.
        </p>
        <Button className="w-full" loading={busy} disabled={friend.account_rub <= 0} onClick={doWithdraw}>Вывести всё</Button>
      </Card>

      {msg && <p className="text-sm text-brand-400">{msg}</p>}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

function KycForm({ friend, onChanged }: { friend: Friend; onChanged: () => void }) {
  const [p, setP] = useState<ProfileIn>({
    first_name: friend.first_name || "",
    last_name: friend.last_name || "",
    middle_name: "",
    date_of_birth: "",
    passport_series: "",
    passport_number: "",
    issued_at: "",
    issued_by: "",
    division_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [uploaded, setUploaded] = useState<Record<string, boolean>>({});
  const [nonResident, setNonResident] = useState(false);
  const [country, setCountry] = useState("");

  const saveProfile = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      await friendsApi.kycProfile(friend.id, {
        ...p,
        residency_country: nonResident ? country.trim() : "",
      });
      setMsg("Данные сохранены");
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };

  const upload = async (kind: KycFileKind, file: File) => {
    setError(""); setMsg("");
    try {
      const b64 = await fileToBase64(file);
      await friendsApi.kycFile(friend.id, kind, file.type || "image/jpeg", b64);
      setUploaded((u) => ({ ...u, [kind]: true }));
    } catch (e) { setError(friendlyError(e, "Не удалось загрузить фото.")); }
  };

  const submit = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      await friendsApi.kycSubmit(friend.id);
      setMsg("Отправлено на проверку и онбординг к брокеру");
      onChanged();
    } catch (e) { setError(friendlyError(e, "Не удалось отправить на проверку.")); } finally { setBusy(false); }
  };

  return (
    <Card className="space-y-3">
      <div className="text-sm font-medium text-white/70">Паспорт друга</div>
      <label className="flex items-center gap-3 text-sm text-white/70">
        <input type="checkbox" checked={nonResident} onChange={(e) => setNonResident(e.target.checked)} className="h-5 w-5 accent-brand" />
        Не резидент РФ (иностранный паспорт)
      </label>
      {nonResident && (
        <Field label="Страна (ISO-2, напр. BY, KZ)" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="BY" />
      )}
      <Field label="Фамилия" value={p.last_name} onChange={(e) => setP({ ...p, last_name: e.target.value })} />
      <Field label="Имя" value={p.first_name} onChange={(e) => setP({ ...p, first_name: e.target.value })} />
      <Field label="Отчество" value={p.middle_name} onChange={(e) => setP({ ...p, middle_name: e.target.value })} />
      <Field label="Дата рождения (ДД.ММ.ГГГГ)" value={p.date_of_birth} onChange={(e) => setP({ ...p, date_of_birth: e.target.value })} placeholder="01.01.1990" />
      <div className="grid grid-cols-2 gap-2">
        <Field label={nonResident ? "Серия (если есть)" : "Серия"} value={p.passport_series} onChange={(e) => setP({ ...p, passport_series: e.target.value })} />
        <Field label="Номер" value={p.passport_number} onChange={(e) => setP({ ...p, passport_number: e.target.value })} />
      </div>
      <Field label="Дата выдачи" value={p.issued_at} onChange={(e) => setP({ ...p, issued_at: e.target.value })} placeholder="01.01.2015" />
      {!nonResident && (
        <Field label="Код подразделения" value={p.division_code} onChange={(e) => setP({ ...p, division_code: e.target.value })} placeholder="770-001" />
      )}
      <Field label={nonResident ? "Кем выдан (орган)" : "Кем выдан"} value={p.issued_by} onChange={(e) => setP({ ...p, issued_by: e.target.value })} />
      <Button variant="ghost" className="w-full" loading={busy} onClick={saveProfile}>Сохранить данные</Button>

      <div className="pt-1 text-sm font-medium text-white/70">Фото</div>
      <UploadRow label="Разворот паспорта" done={uploaded.passport_spread} onFile={(f) => upload("passport_spread", f)} />
      <UploadRow label="Селфи (без паспорта в руках)" done={uploaded.selfie_with_passport} onFile={(f) => upload("selfie_with_passport", f)} />

      {msg && <p className="text-sm text-brand-400">{msg}</p>}
      <ErrorText>{error}</ErrorText>
      <Button className="w-full" loading={busy} onClick={submit}>Отправить на проверку</Button>
    </Card>
  );
}

function UploadRow({ label, done, onFile }: { label: string; done?: boolean; onFile: (f: File) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded-xl bg-surface-raised px-3 py-2.5">
      <span className="text-sm text-white/70">{label}</span>
      <span className={`text-xs ${done ? "text-brand" : "text-white/40"}`}>{done ? "Загружено ✓" : "Выбрать файл"}</span>
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
    </label>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
